import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Mic,
  UserCog,
  Settings as SettingsIcon,
  ChevronDown,
  LogOut,
  Search,
  Activity,
} from "lucide-react";
import { BrandMark } from "../Brand";
import { useAuth } from "../../lib/auth-context";
import { SidebarStartButton, MobileFab } from "../Recording/StartInterviewButton";
import RecoveryBanner from "../RecoveryBanner";
import UpgradeModal from "../UpgradeModal";
import SentryIdentity from "../SentryIdentity";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", hideForHm: true },
  { to: "/interviews", label: "Interviews", icon: Mic, testid: "nav-interviews" },
  { to: "/candidates", label: "Candidates", icon: Users, testid: "nav-candidates", hideForHm: true },
  { to: "/roles", label: "Roles", icon: Briefcase, testid: "nav-roles", hideForHm: true },
  { to: "/team", label: "Team", icon: UserCog, testid: "nav-team", hideForHm: true },
  { to: "/usage", label: "Usage", icon: Activity, testid: "nav-usage", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", hideForHm: true },
];

function Sidebar() {
  const { workspace, member } = useAuth();
  const location = useLocation();
  const isHm = member?.role === "hiring_manager";
  const isFreeSeat = isHm || member?.role === "viewer";
  return (
    <aside
      className="fixed inset-y-0 left-0 hidden w-[244px] flex-col border-r border-ink-200 bg-white lg:flex"
      data-testid="app-sidebar"
    >
      <div className="flex h-16 items-center px-5 border-b border-ink-200">
        <BrandMark />
      </div>
      <div className="px-3 py-4 space-y-3">
        {!isHm && <SidebarStartButton />}
        <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2">
          <div className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
            Workspace
          </div>
          <div className="mt-0.5 truncate text-[13px] font-medium text-ink-900" data-testid="sidebar-workspace-name">
            {workspace?.name || "—"}
          </div>
          <div className="mt-0.5 text-[12px] text-ink-500 capitalize" data-testid="sidebar-role-plan">
            {member?.role?.replace("_", " ") || "—"}
            {!isFreeSeat && <span> · {workspace?.plan || "free"} plan</span>}
          </div>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 px-3">
        {NAV.map((item) => {
          if (item.adminOnly && member?.role !== "admin") return null;
          if (item.hideForHm && member?.role === "hiring_manager") return null;
          const Icon = item.icon;
          const active = location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-[14px] font-medium transition ${
                active
                  ? "bg-emerald-50 text-emerald-900"
                  : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
              }`}
              data-testid={item.testid}
            >
              <Icon
                className={active ? "h-4 w-4 text-emerald-800" : "h-4 w-4 text-ink-500"}
                strokeWidth={1.5}
              />
              {item.label}
            </Link>
          );
        })}
      </nav>
      {!isFreeSeat && (
        <div className="border-t border-ink-200 p-4" data-testid="sidebar-plan-footer">
          <p className="text-[12px] text-ink-500">
            {workspace?.plan === "free" ? `${Math.max(0, 2 - (workspace?.free_interviews_used || 0))} free interviews left` : "Team plan"}
          </p>
        </div>
      )}
    </aside>
  );
}

function Topbar() {
  const { user, member, logout } = useAuth();
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState("");
  const isHm = member?.role === "hiring_manager";
  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchVal.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setSearchVal("");
    }
  };

  return (
    <header
      className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-ink-200 bg-white/90 px-6 backdrop-blur"
      data-testid="app-topbar"
    >
      <div className="flex items-center gap-3 lg:hidden">
        <BrandMark size={24} />
      </div>
      <form onSubmit={submitSearch} className={`relative max-w-md flex-1 hidden md:block ${isHm ? "invisible" : ""}`}>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400"
          strokeWidth={1.5}
        />
        <input
          type="text"
          value={searchVal}
          onChange={(e) => setSearchVal(e.target.value)}
          placeholder="Search candidates, interviews, transcripts…"
          className="hp-input pl-9 pr-12"
          data-testid="topbar-search"
          disabled={isHm}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 hp-kbd">↵</span>
      </form>
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 transition hover:bg-ink-100"
              data-testid="topbar-user-menu"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-800 text-[12px] font-medium text-white">
                {initials}
              </div>
              <span className="hidden text-[13px] font-medium text-ink-900 sm:block">
                {user?.name}
              </span>
              <ChevronDown className="h-4 w-4 text-ink-500" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="text-[13px] font-medium text-ink-900">{user?.name}</div>
              <div className="truncate text-[12px] text-ink-500">{user?.email}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-ink-400">
                {member?.role?.replace("_", " ")}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isHm && (
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <SettingsIcon className="h-4 w-4" strokeWidth={1.5} /> Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={async () => {
                await logout();
                navigate("/signin");
              }}
              data-testid="menu-signout"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function AppShell({ children }) {
  return (
    <div className="min-h-screen bg-ink-50" data-testid="dashboard-shell">
      <Sidebar />
      <div className="lg:pl-[244px]">
        <Topbar />
        <RecoveryBanner />
        <main className="px-6 py-8 lg:px-10">
          <div className="mx-auto w-full max-w-6xl">{children}</div>
        </main>
      </div>
      <MobileFab />
      <UpgradeModal />
      <SentryIdentity />
    </div>
  );
}
